import { CaseUnreadCount, Claim } from '@/api/claims'
import { Tooltip, Typography } from '@/components'
import { getInsuranceCategoryNameKey } from '@/utils/insuranceCategory'
import {
  Badge,
  ColumnDef,
  createColumnHelper,
  SortableHeader,
} from '@surein/ui'
import InsuranceCell from './InsuranceCell'
import TitleCell from './TitleCell'
import { TableCell } from './styles'

const columnHelper = createColumnHelper<Claim>()

const StatusToVariantMap: Record<Claim['status'], string> = {
  open: 'success',
  in_progress: 'warning',
  waiting: 'warning',
  closed: 'default',
}

const createColumns = (
  t: (key: string, options?: Record<string, boolean>) => string,
  companyId: string,
  isClaimsOverviewEnabled: boolean,
  unreadByCase: Record<string, CaseUnreadCount>,
): ColumnDef<Claim, unknown>[] =>
  [
    columnHelper.accessor('title', {
      header: ({ column }) => (
        <SortableHeader column={column}>
          <TableCell margin="-8px 0 -8px -8px">
            {t('claimsPage.table.title')}
          </TableCell>
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <TitleCell
          companyId={companyId}
          claimId={row.original.case_id}
          title={row.original.title || t('claimsPage.table.noTitle')}
          isClickable={isClaimsOverviewEnabled}
          unreadCount={unreadByCase[row.original.case_id]?.total ?? 0}
        />
      ),
    }),
    columnHelper.accessor('status', {
      header: ({ column }) => (
        <SortableHeader column={column}>
          <TableCell margin="-8px 0 -8px -8px">
            {t('claimsPage.table.status')}
          </TableCell>
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <TableCell>
          <Badge
            size="lg"
            isRounded
            variant={StatusToVariantMap[row.original.status]}
          >
            {t(`claimsPage.table.statuses.${row.original.status}`)}
          </Badge>
        </TableCell>
      ),
      size: 140,
    }),
    columnHelper.accessor('insurance', {
      header: ({ column }) => (
        <SortableHeader column={column}>
          <TableCell margin="-8px 0 -8px -8px">
            {t('claimsPage.table.insurance')}
          </TableCell>
        </SortableHeader>
      ),
      cell: ({ row }) => {
        const categoryId = row.original.insurance?.category_id
        const insuranceId = row.original.insurance?.insurance_id

        if (!categoryId || !insuranceId) {
          return (
            <TableCell>
              <Typography
                ellipsis
                title={t('claimsPage.table.noInsurance')}
                variant="p2Body"
              >
                {t('claimsPage.table.noInsurance')}
              </Typography>
            </TableCell>
          )
        }

        return (
          <InsuranceCell
            categoryId={categoryId}
            insuranceId={insuranceId}
            locationId={row.original.location.location_id}
            logoUrl={row.original.insurance?.carrier.logo_url ?? ''}
          />
        )
      },
      sortingFn: (rowA, rowB) => {
        const categoryA = t(
          getInsuranceCategoryNameKey(
            rowA.original.insurance?.category_id ?? '',
          ),
        )
        const categoryB = t(
          getInsuranceCategoryNameKey(
            rowB.original.insurance?.category_id ?? '',
          ),
        )

        return categoryA.localeCompare(categoryB)
      },
      size: 320,
    }),
    columnHelper.accessor('insurance.policy_number', {
      header: ({ column }) => (
        <SortableHeader column={column}>
          <TableCell margin="-8px 0 -8px -8px">
            {t('claimsPage.table.policyNumber')}
          </TableCell>
        </SortableHeader>
      ),
      cell: ({ row }) => {
        const policyNumber = row.original.insurance?.policy_number

        if (!policyNumber) {
          return (
            <TableCell>
              <Tooltip content={t('claimsPage.table.noPolicyNumber')}>
                <Typography
                  ellipsis
                  title={t('claimsPage.table.noPolicyNumber')}
                  variant="p2Body"
                >
                  {t('claimsPage.table.noPolicyNumber')}
                </Typography>
              </Tooltip>
            </TableCell>
          )
        }

        return (
          <TableCell>
            <Tooltip content={policyNumber}>
              <Typography ellipsis title={policyNumber} variant="p2Body">
                {policyNumber}
              </Typography>
            </Tooltip>
          </TableCell>
        )
      },
      sortingFn: (rowA, rowB) => {
        return (rowA.original.insurance?.policy_number || '').localeCompare(
          rowB.original.insurance?.policy_number || '',
        )
      },
      size: 240,
    }),
  ] as Array<ColumnDef<Claim, unknown>>

export default createColumns
